/**
 * Shared vocabulary letting one observation count under equivalent items in
 * different preparation models.
 *
 * Item ids are namespaced per model — `fairplan-class-a-roof` and
 * `ibhs-wf-class-a-roof` describe the same physical work under different ids —
 * and a post stores only its `checklistItemId`, never a model id. Without a
 * shared key, switching models makes an owner's photos vanish from the
 * checklist.
 *
 * A topic is a judgement about the physical work, not a schema nicety. Assign
 * one only when the item names a specific, photographable condition of the
 * property; leave documents, drills and behaviours untagged.
 *
 * Posts are never rewritten. An observation keeps its original
 * `checklistItemId` forever, so provenance survives every model switch.
 *
 * This table is the source of truth: `scripts/seed-checklists.ts` stamps
 * `topic` onto seeded items from it, so the Firestore docs cannot drift.
 * `__tests__/utils/checklistTopics.test.ts` fails the build if they do.
 *
 * Item ids are permanent (posts reference them). Never rename a key.
 */

export const CHECKLIST_TOPICS = [
  // Wildfire-specific.
  'wf-roof-class-a',
  'wf-eaves-enclosed',
  'wf-vents-ember-resistant',
  'wf-windows-upgraded',
  'wf-wall-base-noncombustible',
  'wf-zone0-vegetation-cleared',
  'wf-zone0-noncombustible-improvements',
  'wf-under-deck-clearance',
  'wf-outbuilding-separation',
  'wf-combustible-storage-setback',
  // Storm-specific.
  'storm-roof-wind-secured',
  'storm-roof-sealed-deck',
  'storm-roof-deck-attachment',
  'storm-roof-drip-edge',
  'storm-roof-edge-attachment',
  'storm-roof-wind-rated-cover',
  'storm-vents-wind-driven-rain',
  'storm-loose-items-secured',
  // Work that is the same whatever the hazard. These are the topics that let
  // one photo count for a wildfire model and a storm model at once.
  'gen-gutters-clear',
  'gen-trees-and-brush-cleared',
  'gen-address-visible',
] as const;

export type ChecklistTopic = (typeof CHECKLIST_TOPICS)[number];

/**
 * Report sections, in the order a printed record presents them.
 *
 * Defensible-space zones are how a resident thinks about their property, and
 * how the wildfire standards are written. They are NOT how the checklists are
 * organised: `category` is nine inconsistent strings across five models
 * ("Structure" vs "Structure hardening" vs "Roof" for the same work), and only
 * the two `wf-zone0-*` slugs encode a zone at all. So the mapping lives here,
 * next to the taxonomy it belongs to.
 */
export const REPORT_SECTIONS = [
  { key: 'zone-0', label: 'Zone 0', detail: 'Immediate ignition zone, 0 to 5 feet' },
  { key: 'structure', label: 'Structure hardening', detail: 'Roof, eaves, vents and walls' },
  { key: 'zone-1', label: 'Zone 1', detail: 'Intermediate zone, 5 to 30 feet' },
  { key: 'zone-2', label: 'Zone 2', detail: 'Extended zone, beyond 30 feet' },
  { key: 'access', label: 'Access and response', detail: 'Finding and reaching the property' },
  { key: 'storm', label: 'Wind and storm', detail: 'Storm-specific hardening' },
  { key: 'other', label: 'Other', detail: 'Work outside the zone framework' },
] as const;

export type ReportSectionKey = (typeof REPORT_SECTIONS)[number]['key'];

export interface TopicMeta {
  /** Plain-language name for the work, used when the active model has no
   *  wording of its own for it. Kept short: the model's phrasing wins. */
  label: string;
  section: ReportSectionKey;
}

/**
 * Display name and report section for every topic. A topic is not a checklist
 * item, so it needs a label of its own; a report prefers the active model's
 * wording where that model covers the topic, and falls back to this.
 */
export const TOPIC_META: Record<ChecklistTopic, TopicMeta> = {
  // Zone 0 — the 0 to 5 ft ring, plus what sits in it. Decks belong here:
  // an ember lands under a deck, not at 30 feet.
  'wf-zone0-vegetation-cleared': {
    label: 'Vegetation and combustibles cleared within 5 feet',
    section: 'zone-0',
  },
  'wf-zone0-noncombustible-improvements': {
    label: 'Noncombustible fences, gates and improvements within 5 feet',
    section: 'zone-0',
  },
  'wf-under-deck-clearance': { label: 'Decks and porches clear underneath', section: 'zone-0' },

  // Structure hardening — the building itself.
  'wf-roof-class-a': { label: 'Class-A fire rated roof', section: 'structure' },
  'wf-eaves-enclosed': { label: 'Enclosed eaves and soffits', section: 'structure' },
  'wf-vents-ember-resistant': { label: 'Ember-resistant vents', section: 'structure' },
  'wf-windows-upgraded': { label: 'Upgraded windows or shutters', section: 'structure' },
  'wf-wall-base-noncombustible': { label: 'Noncombustible wall base', section: 'structure' },
  'gen-gutters-clear': { label: 'Roof and gutters clear of debris', section: 'structure' },

  // Zone 1 — 5 to 30 ft.
  'gen-trees-and-brush-cleared': { label: 'Trees trimmed and brush cleared', section: 'zone-1' },

  // Zone 2 — beyond 30 ft. Sheds at 30 feet and firewood at 100 feet are both
  // about keeping a fuel load away from the house.
  'wf-outbuilding-separation': { label: 'Sheds and outbuildings set back', section: 'zone-2' },
  'wf-combustible-storage-setback': {
    label: 'Firewood and combustibles stored away from the house',
    section: 'zone-2',
  },

  // Access.
  'gen-address-visible': { label: 'Address visible from the road', section: 'access' },

  // Storm.
  'storm-roof-wind-secured': { label: 'Roof secured against wind uplift', section: 'storm' },
  'storm-roof-sealed-deck': { label: 'Sealed roof deck', section: 'storm' },
  'storm-roof-deck-attachment': { label: 'Roof deck attachment', section: 'storm' },
  'storm-roof-drip-edge': { label: 'Drip edge at eaves and rakes', section: 'storm' },
  'storm-roof-edge-attachment': { label: 'Edge and starter strip attachment', section: 'storm' },
  'storm-roof-wind-rated-cover': { label: 'Wind-rated roof cover', section: 'storm' },
  'storm-vents-wind-driven-rain': {
    label: 'Vents resistant to wind-driven rain',
    section: 'storm',
  },
  'storm-loose-items-secured': { label: 'Outdoor items secured', section: 'storm' },
};

/** The section a topic reports under. Untopiced items fall to 'other'. */
export function sectionForTopic(topic: ChecklistTopic | undefined): ReportSectionKey {
  return topic ? TOPIC_META[topic].section : 'other';
}

export interface ChecklistItemOrigin {
  /** The shared work this item documents. Absent means never unify. */
  topic?: ChecklistTopic;
  /** Model the id belongs to, used for the "carried over from" line. */
  modelName: string;
}

const FAIR_PLAN = 'California FAIR Plan';
const REDI_BASELINE = 'REDI Baseline';
const IBHS_WILDFIRE = 'IBHS Wildfire Prepared Home';
const IBHS_FORTIFIED = 'IBHS FORTIFIED Roof';
const HURRICANE = 'Hurricane Home Hardening';
const SUMMIT_RIDGE = 'Summit Ridge Wildfire Standard';

/** Reverse index: checklist item id to the work it documents. */
export const CHECKLIST_ITEM_ORIGINS: Record<string, ChecklistItemOrigin> = {
  // --- REDI Baseline (homeowner, hazard: general) ---
  'clear-gutters': { topic: 'gen-gutters-clear', modelName: REDI_BASELINE },
  'vent-screens': { topic: 'wf-vents-ember-resistant', modelName: REDI_BASELINE },
  'zone1-landscape': { topic: 'wf-zone0-vegetation-cleared', modelName: REDI_BASELINE },
  'zone2-landscape': { topic: 'gen-trees-and-brush-cleared', modelName: REDI_BASELINE },
  'firewood-100ft': { topic: 'wf-combustible-storage-setback', modelName: REDI_BASELINE },
  'address-visible': { topic: 'gen-address-visible', modelName: REDI_BASELINE },
  // Packing a bag leaves no photographable trace on the property.
  'go-bag': { modelName: REDI_BASELINE },

  // --- California FAIR Plan (homeowner, hazard: wildfire) ---
  'fairplan-class-a-roof': { topic: 'wf-roof-class-a', modelName: FAIR_PLAN },
  'fairplan-enclosed-eaves': { topic: 'wf-eaves-enclosed', modelName: FAIR_PLAN },
  'fairplan-ember-vents': { topic: 'wf-vents-ember-resistant', modelName: FAIR_PLAN },
  'fairplan-upgraded-windows': { topic: 'wf-windows-upgraded', modelName: FAIR_PLAN },
  'fairplan-noncombustible-wall-base': {
    topic: 'wf-wall-base-noncombustible',
    modelName: FAIR_PLAN,
  },
  'fairplan-vegetation-under-decks': { topic: 'wf-under-deck-clearance', modelName: FAIR_PLAN },
  // FAIR Plan splits the 0-5 ft zone in two: clear the combustibles, then use
  // noncombustible materials. Two topics, or one checklist would show the same
  // photos twice on adjacent rows.
  'fairplan-zone1-5ft': { topic: 'wf-zone0-vegetation-cleared', modelName: FAIR_PLAN },
  'fairplan-noncombustible-zone1': {
    topic: 'wf-zone0-noncombustible-improvements',
    modelName: FAIR_PLAN,
  },
  'fairplan-sheds-30ft': { topic: 'wf-outbuilding-separation', modelName: FAIR_PLAN },
  'fairplan-defensible-space': { topic: 'gen-trees-and-brush-cleared', modelName: FAIR_PLAN },

  // --- IBHS Wildfire Prepared Home (homeowner, hazard: wildfire) ---
  'ibhs-wf-class-a-roof': { topic: 'wf-roof-class-a', modelName: IBHS_WILDFIRE },
  'ibhs-wf-eaves': { topic: 'wf-eaves-enclosed', modelName: IBHS_WILDFIRE },
  'ibhs-wf-vents': { topic: 'wf-vents-ember-resistant', modelName: IBHS_WILDFIRE },
  'ibhs-wf-gutters': { topic: 'gen-gutters-clear', modelName: IBHS_WILDFIRE },
  'ibhs-wf-wall-base': { topic: 'wf-wall-base-noncombustible', modelName: IBHS_WILDFIRE },
  'ibhs-wf-zone0': { topic: 'wf-zone0-vegetation-cleared', modelName: IBHS_WILDFIRE },
  'ibhs-wf-under-deck': { topic: 'wf-under-deck-clearance', modelName: IBHS_WILDFIRE },
  // FAIR Plan's zone-1 item names fences and gates outright, so this is the
  // same requirement. It is NOT the same work as fairplan-sheds-30ft, which is
  // about how far away an outbuilding stands.
  'ibhs-wf-fences': { topic: 'wf-zone0-noncombustible-improvements', modelName: IBHS_WILDFIRE },

  // --- Summit Ridge Mutual (homeowner, hazard: wildfire; fictional, added
  // for the brand-morphing feature) ---
  // Own namespaced ids throughout, per the rule above. Eight share a topic
  // with FAIR Plan's set (evidence unifies across carriers for the same
  // physical work); gutters and address visibility cover ground FAIR Plan's
  // list doesn't.
  'summit-ridge-class-a-roof': { topic: 'wf-roof-class-a', modelName: SUMMIT_RIDGE },
  'summit-ridge-enclosed-eaves': { topic: 'wf-eaves-enclosed', modelName: SUMMIT_RIDGE },
  'summit-ridge-ember-vents': { topic: 'wf-vents-ember-resistant', modelName: SUMMIT_RIDGE },
  'summit-ridge-upgraded-windows': { topic: 'wf-windows-upgraded', modelName: SUMMIT_RIDGE },
  'summit-ridge-wall-base': { topic: 'wf-wall-base-noncombustible', modelName: SUMMIT_RIDGE },
  'summit-ridge-zone1-clearance': { topic: 'wf-zone0-vegetation-cleared', modelName: SUMMIT_RIDGE },
  'summit-ridge-sheds-setback': { topic: 'wf-outbuilding-separation', modelName: SUMMIT_RIDGE },
  'summit-ridge-defensible-space': {
    topic: 'gen-trees-and-brush-cleared',
    modelName: SUMMIT_RIDGE,
  },
  'summit-ridge-gutters-clear': { topic: 'gen-gutters-clear', modelName: SUMMIT_RIDGE },
  'summit-ridge-address-visible': { topic: 'gen-address-visible', modelName: SUMMIT_RIDGE },

  // --- Hurricane Home Hardening (homeowner, hazard: storm) ---
  // Placeholder items per the seed file; Glenn still owes us a real source.
  // "Secured against wind uplift" is too vague to pair with any one FORTIFIED
  // criterion, so it stays on its own.
  'storm-roof-secured': { topic: 'storm-roof-wind-secured', modelName: HURRICANE },
  'storm-trees-trimmed': { topic: 'gen-trees-and-brush-cleared', modelName: HURRICANE },
  'storm-drainage': { topic: 'gen-gutters-clear', modelName: HURRICANE },
  'storm-loose-items': { topic: 'storm-loose-items-secured', modelName: HURRICANE },

  // --- IBHS FORTIFIED Roof (homeowner, hazard: storm) ---
  // Deliberately unshared: these are specific roof construction details with no
  // twin in any other model. ibhs-ft-vents-rain is wind-driven rain resistance,
  // a different requirement from ember-resistant vents.
  'ibhs-ft-sealed-deck': { topic: 'storm-roof-sealed-deck', modelName: IBHS_FORTIFIED },
  'ibhs-ft-ring-shank': { topic: 'storm-roof-deck-attachment', modelName: IBHS_FORTIFIED },
  'ibhs-ft-drip-edge': { topic: 'storm-roof-drip-edge', modelName: IBHS_FORTIFIED },
  'ibhs-ft-edge-attachment': { topic: 'storm-roof-edge-attachment', modelName: IBHS_FORTIFIED },
  'ibhs-ft-wind-rated-cover': { topic: 'storm-roof-wind-rated-cover', modelName: IBHS_FORTIFIED },
  'ibhs-ft-vents-rain': { topic: 'storm-vents-wind-driven-rain', modelName: IBHS_FORTIFIED },

  // --- Business and municipality baselines ---
  // One model each, so there is nothing to unify. Speculative topics here would
  // be dead weight.
  'evac-plan-posted': { modelName: REDI_BASELINE },
  'sprinkler-inspected': { modelName: REDI_BASELINE },
  'perimeter-clear': { modelName: REDI_BASELINE },
  'backup-records': { modelName: REDI_BASELINE },
  'hydrant-access': { modelName: REDI_BASELINE },
  'road-clearance': { modelName: REDI_BASELINE },
  'evac-routes-signed': { modelName: REDI_BASELINE },
  'community-drill': { modelName: REDI_BASELINE },
  'water-supply': { modelName: REDI_BASELINE },
};

/** The model an item id came from, for provenance copy. Undefined when the id
 *  belongs to an agent-authored model, which carries no topics. */
export function modelNameForItem(itemId: string | undefined): string | undefined {
  return itemId ? CHECKLIST_ITEM_ORIGINS[itemId]?.modelName : undefined;
}

/** The shared work an item documents, or undefined when it is unshared. */
export function topicForItem(itemId: string | undefined): ChecklistTopic | undefined {
  return itemId ? CHECKLIST_ITEM_ORIGINS[itemId]?.topic : undefined;
}
